import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Trash2, ExternalLink, Pencil, Pin, PinOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import { formatBytes } from '@/lib/fileToBase64';
import type { RenovationMediaDto, RenovationItemDto } from '@cc/types';
import { KIND_LABEL } from './mediaKinds';

interface Props {
  media: RenovationMediaDto;
  itemsById: Map<string, RenovationItemDto>;
  onEdit: () => void;
  onDelete: () => void;
  onOpen: () => void;
  /** True if this image is the active hero for its room. */
  isHero?: boolean;
  /** Optional handler; when set the pin/unpin button appears on hover. */
  onTogglePin?: () => void;
}

/**
 * Single media tile. For images, renders the presigned URL directly; on
 * load-error (likely an expired presigned URL after 1h staleness) we
 * invalidate the room-media query so it refetches with a fresh URL.
 *
 * For PDFs and unknown types, shows a file-icon card so Karl can still see
 * the filename and click through.
 */
export function MediaThumb({ media, itemsById, onEdit, onDelete, onOpen, isHero, onTogglePin }: Props) {
  const queryClient = useQueryClient();
  const [errored, setErrored] = useState(false);

  const isImage = media.mimeType.startsWith('image/');
  const isPdf = media.mimeType === 'application/pdf';
  const item = media.itemId ? itemsById.get(media.itemId) : null;
  const itemMissing = media.itemId && !item;

  function handleImageError() {
    if (errored) return; // avoid infinite refetch loops
    setErrored(true);
    void queryClient.invalidateQueries({ queryKey: ['room-media', media.roomId] });
  }

  return (
    <div className="group relative overflow-hidden rounded-md border border-border bg-card">
      <button
        onClick={onOpen}
        className="block aspect-square w-full overflow-hidden bg-muted"
        title={media.caption || media.filename}
      >
        {isImage && !errored ? (
          <img
            src={media.url}
            alt={media.caption || media.filename}
            onError={handleImageError}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 p-2 text-muted-foreground">
            <FileText className="h-8 w-8" />
            <span className="line-clamp-2 break-all px-1 text-center text-[10px]">
              {media.filename}
            </span>
            {isPdf && <Badge variant="outline" className="text-[9px]">PDF</Badge>}
          </div>
        )}
      </button>

      <div className="space-y-1 p-1.5">
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant="outline" className="text-[9px]">
            {KIND_LABEL[media.kind]}
          </Badge>
          {item && (
            <Badge variant="outline" className="text-[9px]" title={`Linked to: ${item.activity}`}>
              {truncate(item.activity, 14)}
            </Badge>
          )}
          {itemMissing && (
            <Badge variant="outline" className="text-[9px] text-muted-foreground">
              (item deleted)
            </Badge>
          )}
        </div>
        {media.caption && (
          <p className="line-clamp-2 text-[10px] text-muted-foreground" title={media.caption}>
            {media.caption}
          </p>
        )}
        <p className="text-[9px] text-muted-foreground">{formatBytes(media.sizeBytes)}</p>
      </div>

      <div
        className={cn(
          'absolute right-1 top-1 flex flex-col gap-1 opacity-0 transition-opacity',
          'group-hover:opacity-100 group-focus-within:opacity-100',
        )}
      >
        {onTogglePin && (
          <Button
            size="icon"
            variant="outline"
            className={cn('h-6 w-6', isHero && 'border-primary text-primary')}
            onClick={(e) => {
              e.stopPropagation();
              onTogglePin();
            }}
            title={isHero ? 'Unpin from hub' : 'Pin to hub'}
          >
            {isHero ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
          </Button>
        )}
        <Button
          size="icon"
          variant="outline"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          title="Edit"
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <a
          href={media.url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-accent"
          title="Open"
        >
          <ExternalLink className="h-3 w-3" />
        </a>
        <Button
          size="icon"
          variant="outline"
          className="h-6 w-6"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete"
        >
          <Trash2 className="h-3 w-3 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
